/**
 * Registering the service worker, once, at app start.
 *
 * **Deliberately not inside a component.** The obvious place is the shell, and the shell
 * is behind the auth guard — which would mean the offline shell is only ever installed for
 * someone already signed in, and a cold start at a table with no signal would show the
 * login page's white screen instead of the app. Registration is a property of the origin,
 * not of the session, so it happens in `main.tsx` alongside the render.
 *
 * Dev builds are left alone: a service worker in front of Vite's dev server caches the
 * modules HMR is trying to replace, and the result is an hour spent debugging the cache
 * instead of the code.
 */

/**
 * The registration, or null where there will not be one — a dev build, a browser without
 * service workers, or a failed register. Held at module scope so `useServiceWorker` can
 * observe the same registration this created rather than racing a second `register()`.
 */
let registration: Promise<ServiceWorkerRegistration | null> | null = null

export function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (registration !== null) return registration

  if (!import.meta.env.PROD || !('serviceWorker' in navigator)) {
    registration = Promise.resolve(null)
    return registration
  }

  registration = navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch(() => {
    // A failed registration costs the offline shell and nothing else. The app works, so
    // there is nothing here worth putting in front of a user.
    return null
  })

  return registration
}

/** The registration this app made, if any. Never triggers one. */
export function serviceWorkerRegistration(): Promise<ServiceWorkerRegistration | null> {
  return registration ?? Promise.resolve(null)
}
